import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import SEO from "@/components/app/SEO";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const MarkdownLink = ({ href, children }) => {
  const url = href || "";
  const internal = url.match(/^https?:\/\/resiliationexpresse\.fr(\/.*)?$/);
  if (internal) return <Link to={internal[1] || "/"} className="text-primary hover:underline" data-testid="md-internal-link">{children}</Link>;
  if (url.startsWith("/")) return <Link to={url} className="text-primary hover:underline" data-testid="md-internal-link">{children}</Link>;
  return <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{children}</a>;
};
const MD_COMPONENTS = { a: MarkdownLink };

export default function CategoryPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/categories/${slug}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [slug]);

  // JSON-LD BreadcrumbList + CollectionPage
  useEffect(() => {
    if (!data || data === false || !data.category) return;
    const url = `https://resiliationexpresse.fr/categorie/${slug}`;
    const jsonld = [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://resiliationexpresse.fr/" },
          { "@type": "ListItem", "position": 2, "name": "Annuaire", "item": "https://resiliationexpresse.fr/annuaire" },
          { "@type": "ListItem", "position": 3, "name": data.category.name, "item": url },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": data.category.seo_title || `Résilier ${data.category.name.toLowerCase()}`,
        "description": data.category.seo_intro || data.category.description || "",
        "url": url,
        "inLanguage": "fr-FR",
      },
    ];
    let el = document.getElementById("cat-jsonld");
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = "cat-jsonld";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(jsonld);
    return () => { const e = document.getElementById("cat-jsonld"); if (e) e.remove(); };
  }, [data, slug]);

  if (data === false) return <div className="mx-auto max-w-7xl p-8">Catégorie introuvable.</div>;
  if (!data) return <div className="mx-auto max-w-7xl p-8">Chargement...</div>;

  const catName = data.category.name;
  const seoTitle = data.category.seo_title || `Résilier ${catName.toLowerCase()}`;
  const seoDesc = data.category.seo_intro || `Organismes de la catégorie ${catName} : adresses, procédures et guides pour résilier facilement votre contrat.`;
  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
      <SEO title={seoTitle} description={seoDesc} canonical={`/categorie/${slug}`} noindex={!data.category.seo_enriched} />
      <Link to="/annuaire" className="text-sm text-muted-foreground hover:text-foreground">← Annuaire</Link>
      <h1 className="text-4xl font-bold tracking-tight mt-4 mb-2">{catName}</h1>
      <p className="text-muted-foreground max-w-2xl mb-8">{data.category.seo_intro || data.category.description}</p>

      {data.subcategories.length > 0 && (
        <div className="mb-8">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">Sous-catégories</div>
          <div className="flex flex-wrap gap-2">
            {data.subcategories.map((s) => (
              <Link key={s.slug} to={`/categorie/${s.slug}`} className="px-3 py-1.5 rounded-full border border-border text-sm hover:bg-secondary transition-colors duration-200" data-testid={`subcat-${s.slug}`}>
                {s.name}
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        {data.organizations.map((o) => (
          <Link key={o.slug} to={`/organisme/${o.slug}`} className="card-elevated p-5" data-testid={`cat-org-${o.slug}`}>
            <div className="flex items-start justify-between mb-2">
              <div className="font-semibold">{o.name}</div>
              {o.trust_level === "verified" && <Badge variant="outline" className="text-[10px]">Vérifié</Badge>}
            </div>
            <div className="text-xs text-muted-foreground line-clamp-2">{o.description}</div>
          </Link>
        ))}
      </div>

      {data.category.seo_content && (
        <div className="card-elevated p-6" data-testid="cat-seo-content">
          <div className="prose dark:prose-invert max-w-none leading-relaxed">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
              {data.category.seo_content}
            </ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
}
